import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Box, Typography, Button, Link } from "@mui/material";
import Modal from "../Modal";
import IltClassInfo from "../IltClass/IltClassInfo";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
  fetchSupportInfo,
  changeSupportModalOpenState
} from "../../redux/actions/supportActions";
import LoadingButton from "../Buttons/LoadingButton";
import { displayJoinButton } from "../../utils/course_utils";

const ChangeEnrollmentModalActions = ({
  handleChangeEnrollment,
  onClose,
  isUpdating,
  enrollAndJoin
}) => {
  const { t } = useTranslation();
  return (
    <>
      <Button color="primary" variant="text" onClick={onClose}>
        {t("cancelBtn")}
      </Button>
      <LoadingButton
        color="primary"
        variant="contained"
        onClick={handleChangeEnrollment}
        pending={isUpdating}
      >
        {enrollAndJoin ? t("Enroll & Join") : t("changeEnrollment")}
      </LoadingButton>
    </>
  );
};

const AdminEnrolledModalContent = ({
  assignmentScheduledDate,
  assignmentScheduledTime,
  onContactAdmin,
  showAdminContactLink
}) => {
  const { t } = useTranslation();

  return (
    <Box display="flex" flexDirection="column">
      <Typography variant="body2" color="textSecondary">
        {t("changeEnrollmentModal.assignedToTakeCourseMsg")}
      </Typography>
      <Box mt={2.5} mb={3}>
        <Typography variant="h6">{`${assignmentScheduledDate} at ${assignmentScheduledTime}`}</Typography>
      </Box>
      <Typography variant="body2" color="textSecondary">
        {t("changeEnrollmentModal.enrollInDiffCourseMsg")}{" "}
        {showAdminContactLink ? (
          <Link component="button" onClick={onContactAdmin}>
            {t("changeEnrollmentModal.contactTrainingAdminLink")}
          </Link>
        ) : (
          <>{t("changeEnrollmentModal.contactTrainingAdminLink")}</>
        )}
        .
      </Typography>
    </Box>
  );
};

AdminEnrolledModalContent.propTypes = {
  /**
   * Contact your Training Administrator link
   */
  contactAdminLink: PropTypes.string,
  /**
   * Date in format "DD/MM/YYYY"
   */
  assignmentScheduledDate: PropTypes.string,
  /**
   * Time in format "HH:mm ZZ"
   */
  assignmentScheduledTime: PropTypes.string
};

const ChangeEnrollmentModalContent = ({ currentAssignment, newAssignment }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  return (
    <Box display="flex" flexDirection="column">
      <Typography variant="h6" color="textSecondary">
        {t("changeEnrollmentModal.alreadyEnrolledMsg")}
      </Typography>
      <Box
        display="flex"
        bgcolor={theme.palette.body}
        mb={4}
        mt={2.5}
        py={3}
        pr={2}
        pl={3}
      >
        {currentAssignment && (
          <IltClassInfo course={currentAssignment} showCourseTypeIcon={true} />
        )}
      </Box>
      <Typography variant="h6" color="textSecondary">
        {t("changeEnrollmentModal.changeConfirmationMsg")}
      </Typography>
      <Box
        display="flex"
        bgcolor={theme.palette.body}
        mb={4}
        mt={2.5}
        py={2.25}
        pr={2}
        pl={3}
      >
        {newAssignment && (
          <IltClassInfo course={newAssignment} showCourseTypeIcon={true} />
        )}
      </Box>
    </Box>
  );
};

const ChangeEnrollmentModal = props => {
  const {
    isAdminEnrolled,
    currentAssignment,
    newAssignment,
    open,
    onClose,
    handleChangeEnrollment,
    fetchSupportInfo,
    changeSupportModalOpenState,
    customSupportDisplay,
    isUpdating
  } = props;
  const { t } = useTranslation();

  const onContactAdmin = () => {
    fetchSupportInfo();
    changeSupportModalOpenState(true);
  };

  let enrollAndJoin = newAssignment
    ? !newAssignment.userAssignment?.prerequisites?.length &&
      displayJoinButton(
        newAssignment.zonedStartDate,
        newAssignment.zonedEndDate
      )
    : false;

  if (enrollAndJoin && newAssignment) {
    if (newAssignment.showOverview) {
      enrollAndJoin = "overview";
    } else {
      enrollAndJoin = "first";
    }
  }

  return (
    <Modal
      showCloseIcon
      open={open}
      dialogTitle={
        <Typography variant="h4" color="textPrimary">
          {t("enroll")}
        </Typography>
      }
      onClose={() => onClose(false)}
      dialogContent={
        isAdminEnrolled ? (
          <AdminEnrolledModalContent
            assignmentScheduledDate={currentAssignment.scheduledDate}
            assignmentScheduledTime={currentAssignment.scheduledTime}
            onContactAdmin={onContactAdmin}
            showAdminContactLink={customSupportDisplay.toLowerCase() !== "hide"}
          />
        ) : (
          <ChangeEnrollmentModalContent
            currentAssignment={currentAssignment}
            newAssignment={newAssignment}
          />
        )
      }
      dialogActions={
        isAdminEnrolled ? null : (
          <ChangeEnrollmentModalActions
            handleChangeEnrollment={() => handleChangeEnrollment(enrollAndJoin)}
            onClose={onClose}
            isUpdating={isUpdating}
            enrollAndJoin={enrollAndJoin}
          />
        )
      }
      maxWidth="sm"
      fullWidth
      scrollable
    />
  );
};

const mapStateToProps = state => {
  return {
    customSupportDisplay: state.customerState.customer.customSupportDisplay
  };
};

export default connect(mapStateToProps, {
  fetchSupportInfo,
  changeSupportModalOpenState
})(ChangeEnrollmentModal);

ChangeEnrollmentModal.propTypes = {
  isAdminEnrolled: PropTypes.bool,
  handleChangeEnrollment: PropTypes.func,
  currentAssignment: PropTypes.object,
  newAssignment: PropTypes.object,
  userAssignmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

ChangeEnrollmentModal.defaultProps = {
  open: false
};
